import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import moment from 'moment';

import { AdminFonctionnelControllerApi } from 'lib_api/lib/api/gen';

import ErrorMessageCell from 'components/ErrorMessageCell/ErrorMessageCell';
import { TableWithoutSort } from 'components/WrappedComponents';
import {
  ColumnProps,
  TablePagination,
} from 'components/WrappedComponents/Table/types';
import { DATE_FORMAT } from 'utils/formats';

import {
  FetchFluxWarningResponse,
  FluxAlienationDto,
  FluxAlienationWarningDto,
} from '../types';
import {
  useFetchSingleFluxAlienation,
  useSearchWarningFluxsAlienation,
} from '../utils';

import '../../../css/DashboardWarnings.less';

interface DashboardWarningsAlienationProps<
  FluxDto extends FluxAlienationDto,
  WarningDto extends FluxAlienationWarningDto,
> {
  getControllerFunctionFetchFlux: (
    controller: AdminFonctionnelControllerApi,
  ) => (idFlux: string) => Promise<FluxDto>;
  getControllerFunctionFetchWarnings: (
    controller: AdminFonctionnelControllerApi,
  ) => (
    idFlux: string,
    param: TablePagination,
  ) => Promise<FetchFluxWarningResponse<WarningDto>>;
  buildSpecificTableColumns: () => ColumnProps<WarningDto>[];
  tableTitle: string;
}

function DashboardWarningsAlienation<
  FluxDto extends FluxAlienationDto,
  WarningFluxDto extends FluxAlienationWarningDto,
>({
  getControllerFunctionFetchFlux,
  getControllerFunctionFetchWarnings,
  buildSpecificTableColumns,
  tableTitle,
}: DashboardWarningsAlienationProps<
  FluxDto,
  WarningFluxDto
>): React.ReactElement {
  const params = useParams<Record<string, string>>();
  const idFlux = params.id ?? '';

  const navigate = useNavigate();

  const [flux] = useFetchSingleFluxAlienation(
    idFlux,
    getControllerFunctionFetchFlux,
  );

  const { fetchData, pagination, setPagination, filter, setFilter } =
    useSearchWarningFluxsAlienation(idFlux, getControllerFunctionFetchWarnings);

  function buildColumns(): ColumnProps<WarningFluxDto>[] {
    return [
      ...buildSpecificTableColumns(),
      {
        title: `Message d'erreur`,
        buildCellContent: data => (
          <ErrorMessageCell
            message={data.message}
            title="Message d'erreur Aliénation"
          />
        ),
      },
    ];
  }

  const columns: ColumnProps<WarningFluxDto>[] = React.useMemo(
    () => buildColumns(),
    [],
  );

  return (
    <>
      <div className="dashboardWarningsDossier">
        <div className="headerWarnings">
          <Button
            className="returnButton"
            icon={<ArrowLeftOutlined />}
            onClick={(): void => navigate(-1)}
            type="default"
            shape="round"
            size="middle"
          >
            {'Retour'}
          </Button>
        </div>
        <h1>{tableTitle}</h1>
        <h3>
          Détail sur le flux du {moment(flux?.dateAppel).format(DATE_FORMAT)}
        </h3>
        <div className="tableContainer">
          <TableWithoutSort
            columns={columns}
            fetchData={fetchData}
            buildItemKey={(item): string => item.id}
            pageSizeOptions={[10, 20, 50]}
            pagination={pagination}
            setPagination={setPagination}
            filter={filter}
            setFilter={setFilter}
          />
        </div>
      </div>
    </>
  );
}

export default DashboardWarningsAlienation;
